import camelCase from 'lodash/camelCase'
import React from 'react'

/**
 * Serializes a Contentstack JSON RTE document to renderable elements, matching the output of
 * `@contentstack/json-rte-serializer` for every node type it covers.
 */

export type RTEAttrs = Record<string, any>

export interface RTENode {
  // marks + anything custom
  [key: string]: any
  attrs?: RTEAttrs
  break?: boolean
  children?: RTENode[]
  classname?: string
  id?: string
  text?: string
  type?: string
  uid?: string
}

/** A mark wraps already-rendered child content. `value` is the mark's value (e.g. true, or a color). */
export type MarkRenderer = (child: React.ReactNode, value: any) => React.ReactNode

export interface SerializeOptions {
  /** Render a non-breaking space for otherwise-empty blocks. Mirrors the serializer option. */
  addNbspForEmptyBlocks?: boolean
  /** Per-type attribute names to keep even when empty. Merged onto `{ img: ['alt'], reference: ['alt'] }`. */
  allowedEmptyAttributes?: Record<string, string[]>
  /** Custom marks, merged over the defaults (participate in the same key-order nesting). */
  marks?: Record<string, MarkRenderer>
  /** @internal — set while recursing into <pre>/code so newlines stay literal instead of <br>. */
  preformatted?: boolean
  /**
   * Hook to replace an element node based on its `type`/`attrs`. Return a node to take
   * over rendering, or `undefined` to fall through to the default.
   * `ctx.children` is the already-serialized children; `ctx.getAttributes()` returns the props the
   * node would render with, so an override can reuse its classes/styles/etc.
   */
  renderElement?: (
    node: RTENode,
    ctx: { children: React.ReactNode, getAttributes: () => Record<string, any> },
  ) => React.ReactNode | undefined
}

const DEFAULT_ALLOWED_EMPTY: Record<string, string[]> = { img: ['alt'], reference: ['alt'] }
const FORBIDDEN_ATTR_CHARS = ['"', "'", '>', '<', '/', '=']

// A `reference` node (embedded entry/asset) renders as a tag chosen by its `display-type`, with the
// Contentstack attrs remapped to data-sys-*. `resolveRTEField`/`resolveRTEReference` has already
// injected `href` (or `data-modal-id`) for external-link/page references before serialization.
const REFERENCE_TAGS: Record<string, string> = {
  asset: 'figure',
  block: 'div',
  display: 'figure',
  inline: 'span',
  link: 'a',
}
const REFERENCE_ATTR_MAP: Record<string, string> = {
  'asset-link': 'data-sys-asset-filelink',
  'asset-name': 'data-sys-asset-filename',
  'asset-uid': 'data-sys-asset-uid',
  'content-type': 'data-sys-asset-contenttype',
  'content-type-uid': 'data-sys-content-type-uid',
  'display-type': 'sys-style-type',
  'entry-uid': 'data-sys-entry-uid',
  locale: 'data-sys-entry-locale',
}
const HEADINGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6'])
const PASSTHROUGH = new Set(['doc', 'fragment', 'trgrp'])
const VOID_CELL_TYPES = new Set(['td', 'th'])

// A handful of attributes need specific camelCased prop names.
const REACT_ATTR_NAMES: Record<string, string> = {
  class: 'className',
  for: 'htmlFor',
  colspan: 'colSpan',
  rowspan: 'rowSpan',
  tabindex: 'tabIndex',
  maxlength: 'maxLength',
  readonly: 'readOnly',
  contenteditable: 'contentEditable',
  crossorigin: 'crossOrigin',
  datetime: 'dateTime',
  srcset: 'srcSet',
  usemap: 'useMap',
}

const DEFAULT_MARKS: Record<string, MarkRenderer> = {
  bold: child => <strong>{child}</strong>,
  italic: child => <em>{child}</em>,
  underline: child => <u>{child}</u>,
  strikethrough: child => <del>{child}</del>,
  superscript: child => <sup>{child}</sup>,
  subscript: child => <sub>{child}</sub>,
  inlineCode: child => <span data-type="inlineCode">{child}</span>,
}

const isTextNode = (node: RTENode): boolean => Object.prototype.hasOwnProperty.call(node, 'text')

/** Split text on newlines, interspersing <br/> — mirrors the serializer's `\n` → `<br/>`. */
const textWithBreaks = (text: string): React.ReactNode => {
  if (!text.includes('\n')) {
    return text
  }

  const parts = text.split('\n')
  const out: React.ReactNode[] = []
  parts.forEach((part, index) => {
    if (index > 0) {
      // eslint-disable-next-line react/no-array-index-key
      out.push(<br key={`br-${index}`} />)
    }
    if (part) {
      out.push(part)
    }
  })

  return out
}

const reactStyleKey = (key: string): string => (key.startsWith('--') ? key : camelCase(key))

const serializeText = (node: RTENode, options: SerializeOptions, key: React.Key): React.ReactNode => {
  let content: React.ReactNode = options.preformatted ? (node.text ?? '') : textWithBreaks(node.text ?? '')

  // classname/id on the text node → wrapping span (serializer wraps before marks).
  if (node.classname || node.id) {
    content = (
      <span className={node.classname || undefined} id={node.id || undefined}>
        {content}
      </span>
    )
  }

  // Marks wrap in object-key order; the last matching key ends up OUTERMOST.
  const marks = options.marks ? { ...DEFAULT_MARKS, ...options.marks } : DEFAULT_MARKS
  Object.keys(node).forEach((markKey) => {
    if (Object.prototype.hasOwnProperty.call(marks, markKey)) {
      content = marks[markKey](content, node[markKey])
    }
  })

  return <React.Fragment key={key}>{content}</React.Fragment>
}

/**
 * Mirror of the serializer's attribute algorithm, producing a props object.
 * Attribute *order* is intentionally not preserved (irrelevant to the resulting DOM).
 */
const buildProps = (node: RTENode, options: SerializeOptions): { props: Record<string, any>, tag?: string } => {
  const type = node.type || ''
  // Deep clone so we never mutate caller data (the serializer does the same).
  const attrs: RTEAttrs = node.attrs ? JSON.parse(JSON.stringify(node.attrs)) : {}
  const props: Record<string, any> = {}
  const style: Record<string, string> = {}

  // `redactor-attributes` contents become real attributes; the wrapper key is dropped.
  if (attrs['redactor-attributes'] && typeof attrs['redactor-attributes'] === 'object') {
    Object.assign(attrs, attrs['redactor-attributes'])
  }
  delete attrs['redactor-attributes']

  // `url` is used only to derive href/src and is then dropped.
  if (attrs.url != null) {
    if (type === 'a') {
      attrs.href = attrs.url
    } else if (type === 'img') {
      attrs.src = attrs.url
    }
  }
  delete attrs.url

  // `orgType` overrides the rendered tag and is dropped from output.
  const tag = typeof attrs.orgType === 'string' ? attrs.orgType : undefined
  delete attrs.orgType

  // `class-name` → class.
  if (attrs['class-name'] != null) {
    attrs.class = attrs['class-name']
    delete attrs['class-name']
  }

  // `style` object → style object (kebab/camel keys normalized). Not for img (serializer skips it).
  if (attrs.style && typeof attrs.style === 'object' && !Array.isArray(attrs.style) && type !== 'img') {
    Object.entries(attrs.style as Record<string, any>).forEach(([styleKey, styleValue]) => {
      if (styleValue === '' || styleValue == null) {
        return
      }
      style[reactStyleKey(styleKey)] = String(styleValue)
    })
  }
  delete attrs.style

  // Table-internal attrs the serializer strips (colSpan/rowSpan are dropped, not emitted).
  // Note: rows/cols/colWidths ARE kept and emitted as attributes.
  delete attrs.disabledCols
  delete attrs.colSpan
  delete attrs.rowSpan
  delete attrs.void

  const allowedEmpty = { ...DEFAULT_ALLOWED_EMPTY, ...options.allowedEmptyAttributes }

  Object.entries(attrs).forEach(([attrKey, attrValue]) => {
    if (FORBIDDEN_ATTR_CHARS.some(char => attrKey.includes(char))) {
      return
    }
    // Drop nested objects; arrays (e.g. colWidths) are stringified like the serializer does.
    if (attrValue !== null && typeof attrValue === 'object' && !Array.isArray(attrValue)) {
      return
    }
    const keepEmpty = allowedEmpty[type]?.includes(attrKey)
    if (!keepEmpty && (attrValue == null || attrValue === '')) {
      return
    }
    props[REACT_ATTR_NAMES[attrKey] || attrKey] = attrValue === '' ? '' : String(attrValue)
  })

  if (Object.keys(style).length > 0) {
    props.style = style
  }

  return { props, tag }
}

/** Build a `<colgroup>` when a table's column widths aren't all equal (mirrors the serializer). */
const renderColgroup = (node: RTENode): React.ReactNode => {
  const colWidths: number[] | undefined = node.attrs?.colWidths
  if (!Array.isArray(colWidths) || colWidths.length === 0) {
    return null
  }
  const sum = colWidths.reduce((acc, width) => acc + width, 0)
  const cols = node.attrs?.cols
  // The serializer skips the colgroup only when `attrs.cols` is set and the widths are all equal.
  const allEqual = new Set(colWidths).size === 1 && typeof cols === 'number' && cols * colWidths[0] === sum
  if (allEqual || sum === 0) {
    return null
  }

  return (
    <colgroup data-width={String(sum)}>
      {colWidths.map((width, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <col key={index} style={{ width: `${(width / sum) * 100}%` }} />
      ))}
    </colgroup>
  )
}

const serializeElement = (node: RTENode, options: SerializeOptions, key: React.Key): React.ReactNode => {
  const type = node.type || ''

  const childOptions = type === 'code' ? { ...options, preformatted: true } : options
  // serializeNode/renderDefaultElement are mutually recursive with this function.
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const renderedChildren = (node.children || []).map((child, index) => serializeNode(child, childOptions, index))

  // Empty-block nbsp: when enabled and there are no (non-empty) children.
  const hasContent = (node.children || []).some(child => (isTextNode(child) ? child.text !== '' : true))
  const children: React.ReactNode = !hasContent && options.addNbspForEmptyBlocks ? ' ' : renderedChildren

  if (options.renderElement) {
    const getAttributes = () => buildProps(node, options).props
    const overridden = options.renderElement(node, { children, getAttributes })
    if (overridden !== undefined) {
      // Carry the node's key so an override result placed in a mapped child list stays keyed.
      return <React.Fragment key={key}>{overridden}</React.Fragment>
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return renderDefaultElement(node, type, children, options, key)
}

const renderDefaultElement = (
  node: RTENode,
  type: string,
  children: React.ReactNode,
  options: SerializeOptions,
  key: React.Key,
): React.ReactNode => {
  if (PASSTHROUGH.has(type)) {
    return <React.Fragment key={key}>{children}</React.Fragment>
  }

  // td/th with a truthy `void` attr are omitted entirely.
  if (VOID_CELL_TYPES.has(type) && node.attrs?.void) {
    return null
  }

  // hr ignores attrs and children: always a bare <hr>.
  if (type === 'hr') {
    return <hr key={key} />
  }

  // check-list keeps only its forced data attributes (the serializer reads `node.checked`).
  if (type === 'check-list') {
    return <p key={key} data-checked={String(node.checked)} data-type="checked">{children}</p>
  }

  const { props, tag: orgTag } = buildProps(node, options)

  if (type === 'img') {
    // `alt` (when present) is supplied via `props`; eslint can't detect it statically.
    // eslint-disable-next-line jsx-a11y/alt-text
    return <img key={key} {...props} />
  }

  // code blocks render as <pre>.
  if (type === 'code') {
    return <pre key={key} {...props}>{children}</pre>
  }

  if (type === 'table') {
    return (
      <table key={key} {...props}>
        {renderColgroup(node)}
        {children}
      </table>
    )
  }

  // reference: pick the tag from display-type. Only entry/asset references get their attrs remapped
  // to Contentstack data-sys-*; other references (dynamic variables, modal/page links) keep attrs as-is.
  if (type === 'reference') {
    const refAttrs = node.attrs || {}
    const refTag = REFERENCE_TAGS[refAttrs['display-type'] as string] ?? 'span'
    const isEntryOrAsset = refAttrs.type === 'entry' || refAttrs.type === 'asset'
    const mapped: RTEAttrs = {}
    Object.entries(refAttrs).forEach(([attrKey, attrValue]) => {
      mapped[isEntryOrAsset ? (REFERENCE_ATTR_MAP[attrKey] ?? attrKey) : attrKey] = attrValue
    })
    const RefTag = refTag as keyof React.JSX.IntrinsicElements
    return <RefTag key={key} {...buildProps({ attrs: mapped, type: refTag }, options).props}>{children}</RefTag>
  }

  // A span that only wraps a single span and carries no attributes collapses to its child.
  if (
    type === 'span'
    && Object.keys(props).length === 0
    && node.children?.length === 1
    && node.children[0]?.type === 'span'
  ) {
    return <React.Fragment key={key}>{children}</React.Fragment>
  }

  const Tag = (orgTag || type) as keyof React.JSX.IntrinsicElements

  // Unknown types with no orgType: emit children only (matches serializer's "join children, no wrapper").
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  if (!orgTag && !KNOWN_ELEMENT_TYPES.has(type)) {
    return <React.Fragment key={key}>{children}</React.Fragment>
  }

  return <Tag key={key} {...props}>{children}</Tag>
}

const KNOWN_ELEMENT_TYPES = new Set<string>([
  'p',
  'blockquote',
  'ol',
  'ul',
  'li',
  'a',
  'div',
  'span',
  'thead',
  'tbody',
  'tr',
  'td',
  'th',
  ...HEADINGS,
])

const serializeNode = (node: RTENode, options: SerializeOptions, key: React.Key): React.ReactNode => {
  if (isTextNode(node)) {
    return serializeText(node, options, key)
  }
  return serializeElement(node, options, key)
}

/** Serialize a JSON RTE document (or any node) to renderable elements. */
export const jsonToReact = (value: RTENode | null | undefined, options: SerializeOptions = {}): React.ReactNode => {
  if (!value) {
    return null
  }
  return serializeNode(value, options, 0)
}

const TEXT_BLOCK_TYPES = new Set(['blockquote', 'br', 'code', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'li', 'p', 'tr'])

/** Collect the plain-text content of a JSON RTE document — block-level nodes are space-separated. */
export const jsonToText = (value: RTENode | null | undefined): string => {
  const collect = (node: any): string => {
    if (!node || typeof node !== 'object') {
      return ''
    }
    if (Array.isArray(node)) {
      return node.map(collect).join('')
    }
    if (typeof node.text === 'string') {
      return node.text
    }
    const inner = collect(node.children)
    return TEXT_BLOCK_TYPES.has(node.type) ? `${inner} ` : inner
  }

  return collect(value).replace(/\s+/g, ' ').trim()
}
